import { useEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import { streamSse } from '../lib/api';
import { useAuth } from '../stores/auth';
import { LotusMark } from './site/Logo';
import { Sheet } from './ui';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Tools the model actually invoked, shown so the user can see what it did. */
  toolCalls?: Array<{ name: string; status: 'running' | 'done' | 'approval' }>;
}

/**
 * The assistant. Streams over SSE so tokens appear as they are generated, and shows
 * each tool call as it happens — which is the point: the model is not answering from
 * memory, it is calling permission-checked tools against live data.
 */
export function AssistantDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const user = useAuth((s) => s.user);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);
  useEffect(() => () => abortRef.current?.abort(), []);

  async function send(preset?: string) {
    const text = (preset ?? input).trim();
    if (!text || streaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '', toolCalls: [] }]);
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const patchLast = (fn: (m: ChatMessage) => ChatMessage) =>
      setMessages((prev) => { const next = [...prev]; const last = next[next.length - 1]; if (last) next[next.length - 1] = fn(last); return next; });

    try {
      await streamSse('/ai/chat', { conversationId, message: text }, {
        signal: controller.signal,
        onEvent: (event, data) => {
          const d = data as Record<string, unknown>;
          switch (event) {
            case 'conversation': setConversationId(String(d.conversationId ?? '')); break;
            case 'token': patchLast((m) => ({ ...m, content: m.content + String(d.text ?? '') })); break;
            case 'tool_call': patchLast((m) => ({ ...m, toolCalls: [...(m.toolCalls ?? []), { name: String(d.name ?? 'tool'), status: 'running' }] })); break;
            case 'tool_result': patchLast((m) => ({ ...m, toolCalls: (m.toolCalls ?? []).map((t) => (t.name === d.name ? { ...t, status: 'done' } : t)) })); break;
            case 'approval_required': patchLast((m) => ({ ...m, toolCalls: (m.toolCalls ?? []).map((t) => (t.name === d.name ? { ...t, status: 'approval' } : t)), content: m.content + `\n\nThis action needs approval before it runs.` })); break;
            case 'error': patchLast((m) => ({ ...m, content: m.content + `\n\n${String(d.message ?? 'Something went wrong.')}` })); break;
          }
        },
      });
    } catch (err) {
      patchLast((m) => ({ ...m, content: m.content || (err instanceof Error ? err.message : 'The assistant is unavailable.') }));
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }

  const suggestions = user?.allAccess
    ? ['How is Elgar House performing this year?', 'Which tenants owe us money?', 'Which certificates expire in the next 60 days?', 'Build me an arrears dashboard']
    : user?.scope.type === 'OWN'
      ? ['What jobs are assigned to me today?', 'Show my overdue jobs', 'What do I owe on my account?']
      : ['Which units are vacant right now?', 'Show overdue work orders', 'Rent collected this month'];

  if (!open) return null;

  return (
    <Sheet open onClose={onClose} raw size="sm" label="Ask Lotus" className="h-[92dvh] sm:h-auto">
        <div className="safe-top flex h-16 shrink-0 items-center justify-between border-b border-ink-100 px-5">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-full bg-ink-100"><LotusMark className="h-5 w-5" /></div>
            <div className="leading-tight">
              <div className="text-[15px] font-semibold text-ink-900">Ask Lotus</div>
              <div className="text-[11px] text-ink-400">Live data · only what your role can see</div>
            </div>
          </div>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg text-ink-500 transition hover:bg-ink-100 hover:text-ink-900"><Icons.X className="h-4 w-4" /></button>
        </div>

        <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-ink-50/60 p-5">
          {messages.length === 0 && (
            <div className="pt-4">
              <p className="text-[20px] font-semibold leading-tight tracking-[-0.02em] text-ink-900">What would you like to know, {user?.firstName ?? 'there'}?</p>
              <p className="mt-2 text-[13px] leading-relaxed text-ink-500">Ask about the portfolio, a tenancy, a work order or the numbers. Every answer comes from a tool call you can see, never from memory.</p>
              <div className="mt-6 space-y-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => void send(s)} className="flex w-full items-center gap-2.5 rounded-lg border border-ink-200 bg-white px-3.5 py-2.5 text-left text-[13px] text-ink-700 transition hover:border-ink-300 hover:bg-ink-50">
                    <Icons.Sparkles className="h-3.5 w-3.5 shrink-0 text-bronze-500" />{s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex'}>
              <div className={m.role === 'user' ? 'max-w-[85%] rounded-xl rounded-br-md bg-bronze-500 px-4 py-2.5 text-[13.5px] text-on-primary' : 'max-w-[94%] rounded-xl rounded-bl-md border border-ink-200 bg-white px-4 py-3 text-[13.5px] text-ink-800'}>
                {m.toolCalls && m.toolCalls.length > 0 && (
                  <div className="mb-2.5 flex flex-wrap gap-1.5">
                    {m.toolCalls.map((t, j) => (
                      <span key={j} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[10.5px] ${t.status === 'approval' ? 'border-amber-200 bg-amber-50 text-amber-700' : t.status === 'done' ? 'border-ink-200 bg-ink-50 text-ink-600' : 'border-bronze-200 bg-bronze-50 text-bronze-700'}`}>
                        {t.status === 'running' && <Icons.Loader2 className="h-3 w-3 animate-spin" />}
                        {t.status === 'done' && <Icons.Check className="h-3 w-3 text-bronze-500" />}
                        {t.status === 'approval' && <Icons.ShieldAlert className="h-3 w-3" />}
                        {t.name}
                      </span>
                    ))}
                  </div>
                )}
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                {m.role === 'assistant' && !m.content && streaming && <span className="inline-block h-3.5 w-1.5 animate-pulse rounded-sm bg-bronze-400" />}
              </div>
            </div>
          ))}
        </div>

        <div className="safe-bottom [--safe-pad:0.75rem] shrink-0 border-t border-ink-100 bg-white p-3">
          <div className="flex items-end gap-2 rounded-lg border border-ink-200 bg-white p-1.5 transition focus-within:border-bronze-500 focus-within:ring-2 focus-within:ring-bronze-500/30">
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
              placeholder="Ask anything about the portfolio…"
              className="max-h-32 flex-1 resize-none bg-transparent px-2.5 py-2 text-[13.5px] text-ink-900 outline-none placeholder:text-ink-400"
            />
            <button onClick={() => void send()} disabled={streaming || !input.trim()} className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-bronze-500 text-on-primary transition hover:bg-bronze-600 disabled:opacity-30">
              <Icons.ArrowUp className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2 px-1 text-[10.5px] text-ink-400">High-risk actions (payments, approvals) are queued for a human to approve.</p>
        </div>
    </Sheet>
  );
}
